import { useMemo, useState } from 'react';
import { Braces, Dices, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEditor } from '../../state/store';
import { VariableDef, VariableType, VariableRegistry } from '../../runtime/Variables';
import { compileExpression } from '../../runtime/Expressions';
import { VARIABLE_PRESETS, presetGroups } from '../../runtime/presets';
import { CellWrite } from '../../tools/types';
import { Checkbox, Field, Hint, Modal, NumberInput, Panel } from '../widgets/common';
import { Select } from '../widgets/primitives';

const TYPES: VariableType[] = [
  'string',
  'integer',
  'float',
  'boolean',
  'percentage',
  'duration',
  'timestamp',
  'array',
  'computed',
];

const MOCK_NAMES = ['Alice', 'Bob', 'Charlie', 'Dana', 'Eli', 'Farah', 'Gus', 'Hana'];
const MOCK_SERVERS = ['EU-01', 'US-WEST-2', 'AP-SOUTH-1', 'DE-FRA-3'];

/** Plausible preview data, so a design can be judged with realistic content. */
export function mockValueFor(def: VariableDef): string | number | boolean {
  const name = def.name.toLowerCase();
  switch (def.type) {
    case 'integer':
      if (name.includes('port')) return 22;
      if (name.includes('ping') || name.includes('latency')) return 10 + Math.floor(Math.random() * 90);
      return Math.floor(Math.random() * 1000);
    case 'float':
      return Math.round(Math.random() * 10000) / 100;
    case 'percentage':
      return Math.floor(Math.random() * 101);
    case 'duration':
      return Math.floor(Math.random() * 900000);
    case 'timestamp':
      return Date.now();
    case 'boolean':
      return Math.random() > 0.5;
    case 'array':
      return MOCK_NAMES.slice(0, 3).join(', ');
    default:
      if (name.includes('server') || name.includes('host')) {
        return MOCK_SERVERS[Math.floor(Math.random() * MOCK_SERVERS.length)];
      }
      if (name.includes('user') || name.includes('name')) {
        return MOCK_NAMES[Math.floor(Math.random() * MOCK_NAMES.length)];
      }
      return 'sample';
  }
}

export function VariablesPanel(): JSX.Element {
  const revision = useEditor((s) => s.revision);
  const showPreview = useEditor((s) => s.showVariablePreview);
  // Subscribed so the "where it lands" line below follows the pointer, the
  // text caret and the selection as they move.
  useEditor((s) => s.cursor);
  useEditor((s) => s.caret);
  const store = useEditor.getState();
  const tab = store.active();
  const [editing, setEditing] = useState<VariableDef | null>(null);
  const [creating, setCreating] = useState(false);
  const [showPresets, setShowPresets] = useState(false);

  const resolved = useMemo(
    () => new VariableRegistry(tab.doc.variables).resolveScope(tab.doc.previewValues, true),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [revision],
  );
  const registry = useMemo(() => new VariableRegistry(tab.doc.variables), [revision]);

  /**
   * Writes `{{name}}` onto the canvas.
   *
   * This is the step that was missing: without it a variable was an abstract
   * entry in a list, and you had to know to type the braces yourself.
   */
  const insertAt = store.insertOrigin();
  const insertTarget = tab.selection.getBounds()
    ? 'the top-left of the selection'
    : store.caret
      ? 'the text caret'
      : store.cursor
        ? 'the cell under the pointer'
        : 'the top-left corner';

  const insert = (name: string) => {
    const state = useEditor.getState();
    const origin = state.insertOrigin();
    const brush = state.brush;
    const writes: CellWrite[] = [...`{{${name}}}`].map((ch, i) => ({
      x: origin.x + i,
      y: origin.y,
      cell: { char: ch, fg: brush.fg, bg: brush.bg, attrs: brush.attrs },
    }));
    state.writeCells(writes, `Insert {{${name}}}`);
    state.notify(`Placed {{${name}}} at ${origin.x}, ${origin.y}.`, 'success');
  };

  return (
    <>
      <Panel title="Variables" collapsible={false}>
        <Hint>
          A variable is a placeholder you put on the canvas, like <code>{'{{username}}'}</code>. The
          editor shows sample data in its place; your application supplies the real value when it
          renders the design.
        </Hint>
        <Hint>
          <b>Insert</b> writes it at{' '}
          <code className="mono">
            {insertAt.x}, {insertAt.y}
          </code>{' '}
          — {insertTarget}.
        </Hint>
        <div className="grid2" style={{ marginTop: 8 }}>
          <button className="btn solid" onClick={() => setShowPresets(true)}>
            <Plus size={14} /> Add common
          </button>
          <button className="btn solid" onClick={() => setCreating(true)}>
            <Braces size={14} /> Custom
          </button>
        </div>
      </Panel>

      <Panel
        title={`In this design (${tab.doc.variables.length})`}
        collapsible={false}
        actions={
          tab.doc.variables.length > 0 ? (
            <button
              className="icon-btn"
              title="Fill the sample values with plausible data"
              onClick={() => {
                tab.doc.variables.forEach((v) => {
                  if (v.type !== 'computed') store.setPreviewValue(v.name, mockValueFor(v));
                });
                store.notify('Generated sample data.', 'success');
              }}
            >
              <Dices size={14} />
            </button>
          ) : null
        }
      >
        {tab.doc.variables.length === 0 ? (
          <div className="empty-state">
            <Braces size={20} />
            <span>No variables yet</span>
            <span className="faint">Add one above, then press Insert to place it.</span>
          </div>
        ) : (
          <div className="list" key={revision} style={{ maxHeight: 340 }}>
            {tab.doc.variables.map((v) => {
              const formatted = registry.format(v.name, resolved);
              return (
                <div key={v.name} className="card" style={{ padding: '8px 9px' }}>
                  <div className="row">
                    <code className="mono" style={{ color: 'var(--accent)' }}>
                      {`{{${v.name}}}`}
                    </code>
                    <span className="spacer" />
                    {v.width ? <span className="tag">max {v.width}</span> : null}
                  </div>

                  <div className="row" style={{ marginTop: 5 }}>
                    <span className="faint" style={{ width: 38 }}>
                      shows
                    </span>
                    <span
                      className="mono"
                      style={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'pre',
                        background: 'var(--bg-0)',
                        borderRadius: 3,
                        padding: '1px 5px',
                        color:
                          formatted.color >= 0
                            ? `#${formatted.color.toString(16).padStart(6, '0')}`
                            : 'var(--text)',
                      }}
                    >
                      {formatted.text || ' '}
                    </span>
                  </div>

                  <div className="row" style={{ marginTop: 6, gap: 4 }}>
                    <button
                      className="btn solid"
                      style={{ flex: 1, height: 26 }}
                      onClick={() => insert(v.name)}
                      title={`Write {{${v.name}}} onto the canvas`}
                    >
                      Insert
                    </button>
                    <button className="icon-btn" title="Edit" onClick={() => setEditing(v)}>
                      <Pencil size={13} />
                    </button>
                    <button
                      className="icon-btn"
                      title="Delete"
                      onClick={() => store.removeVariable(v.name)}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      {tab.doc.variables.some((v) => v.type !== 'computed') ? (
        <Panel title="Sample values">
          <Hint>Only for designing — these never leave the editor.</Hint>
          <div style={{ marginTop: 6 }}>
            {tab.doc.variables
              .filter((v) => v.type !== 'computed')
              .map((v) => (
                <label className="field" key={v.name}>
                  <span className="mono">{v.name}</span>
                  <input
                    className="input"
                    value={String(tab.doc.previewValues[v.name] ?? '')}
                    placeholder={String(v.previewValue ?? v.defaultValue ?? '')}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const numeric = Number(raw);
                      store.setPreviewValue(
                        v.name,
                        raw !== '' && Number.isFinite(numeric) && v.type !== 'string' ? numeric : raw,
                      );
                    }}
                  />
                </label>
              ))}
          </div>
          <Checkbox
            label="Show values on the canvas"
            checked={showPreview}
            onChange={() => store.toggleVariablePreview()}
            title="Off shows the raw {{name}} placeholders instead"
          />
          <Hint>
            The canvas keeps the placeholder text either way — values are drawn over it, so a design
            exported as a template still contains {'{{name}}'}.
          </Hint>
        </Panel>
      ) : null}

      {showPresets && (
        <PresetPicker
          existing={tab.doc.variables.map((v) => v.name)}
          onPick={(def) => {
            const state = useEditor.getState();
            if (state.active().doc.variables.some((v) => v.name === def.name)) return;
            state.upsertVariable({ ...def });
            if (def.previewValue !== undefined) state.setPreviewValue(def.name, def.previewValue);
            state.notify(`Added {{${def.name}}}.`, 'success');
          }}
          onClose={() => setShowPresets(false)}
        />
      )}

      {(editing || creating) && (
        <VariableEditor
          initial={
            editing ?? {
              name: `value_${tab.doc.variables.length + 1}`,
              type: 'string',
              defaultValue: '',
              previewValue: 'sample',
              fallback: '',
              align: 'left',
            }
          }
          existingNames={tab.doc.variables.map((v) => v.name)}
          isNew={!editing}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
          onSave={(def, previousName) => {
            store.upsertVariable(def, previousName);
            if (def.previewValue !== undefined) store.setPreviewValue(def.name, def.previewValue);
            setEditing(null);
            setCreating(false);
          }}
        />
      )}
    </>
  );
}

/** One-click variables for the things dashboards nearly always show. */
function PresetPicker({
  existing,
  onPick,
  onClose,
}: {
  existing: string[];
  onPick: (def: VariableDef) => void;
  onClose: () => void;
}): JSX.Element {
  return (
    <Modal
      title="Add a variable"
      onClose={onClose}
      width={560}
      footer={
        <button className="btn primary" onClick={onClose}>
          Done
        </button>
      }
    >
      <Hint>Each one arrives with a sensible type, width and formatting already set.</Hint>
      {presetGroups().map((group) => (
        <div key={group} style={{ marginTop: 12 }}>
          <div className="group-title">{group}</div>
          <div className="group" style={{ gap: 5, marginTop: 6 }}>
            {VARIABLE_PRESETS.filter((p) => p.group === group).map((preset) => {
              const already = existing.includes(preset.def.name);
              return (
                <button
                  key={preset.def.name}
                  className="option"
                  disabled={already}
                  onClick={() => onPick(preset.def)}
                  title={already ? 'Already in this design' : preset.blurb}
                >
                  <code className="mono" style={{ color: already ? 'inherit' : 'var(--accent)' }}>
                    {`{{${preset.def.name}}}`}
                  </code>
                  <span className="label faint" style={{ textAlign: 'right' }}>
                    {already ? 'added' : preset.blurb}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </Modal>
  );
}

function VariableEditor({
  initial,
  existingNames,
  isNew,
  onClose,
  onSave,
}: {
  initial: VariableDef;
  existingNames: string[];
  isNew: boolean;
  onClose: () => void;
  onSave: (def: VariableDef, previousName?: string) => void;
}): JSX.Element {
  const [def, setDef] = useState<VariableDef>({ ...initial });
  const previousName = initial.name;
  const update = (patch: Partial<VariableDef>) => setDef({ ...def, ...patch });

  const nameValid = /^[A-Za-z_][A-Za-z0-9_]*$/.test(def.name);
  const nameTaken = def.name !== previousName && existingNames.includes(def.name);
  let exprError: string | null = null;
  if (def.type === 'computed') {
    try {
      compileExpression(def.expression ?? '0');
    } catch (e) {
      exprError = (e as Error).message;
    }
  }
  const canSave = nameValid && !nameTaken && !exprError;

  return (
    <Modal
      title={isNew ? 'New variable' : `Edit ${previousName}`}
      onClose={onClose}
      width={460}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!canSave}
            onClick={() => onSave(def, isNew ? undefined : previousName)}
          >
            Save
          </button>
        </>
      }
    >
      <Field label="Show">
        <input
          className="input mono"
          value={def.name}
          placeholder="username"
          onChange={(e) => update({ name: e.target.value })}
        />
      </Field>
      <Hint>
        This is the name you place on the canvas: <code>{`{{${def.name || 'name'}}}`}</code>
      </Hint>
      {!nameValid ? (
        <div className="error-text" style={{ marginBottom: 6 }}>
          Use letters, digits and underscore; must not start with a digit.
        </div>
      ) : null}
      {nameTaken ? (
        <div className="error-text" style={{ marginBottom: 6 }}>
          A variable with this name already exists.
        </div>
      ) : null}

      <Field label="Kind of value">
        <Select
          ariaLabel="Kind of value"
          value={def.type}
          options={TYPES.map((t) => ({ value: t, label: t }))}
          onChange={(type) => update({ type })}
        />
      </Field>

      {def.type === 'computed' ? (
        <>
          <Field label="Expression">
            <input
              className="input mono"
              placeholder="online / capacity * 100"
              value={def.expression ?? ''}
              onChange={(e) => update({ expression: e.target.value })}
            />
          </Field>
          {exprError ? <div className="error-text">{exprError}</div> : null}
          <p className="faint" style={{ lineHeight: 1.5 }}>
            Safe expression only — arithmetic, comparisons, ternaries and the helpers min, max, round,
            floor, ceil, abs, clamp, sqrt, pow, len, number, string, if. No code is executed.
          </p>
        </>
      ) : (
        <>
          <Field label="Default">
            <input
              className="input"
              value={String(def.defaultValue ?? '')}
              onChange={(e) => update({ defaultValue: coerce(e.target.value, def.type) })}
            />
          </Field>
          <Field label="Preview">
            <input
              className="input"
              value={String(def.previewValue ?? '')}
              onChange={(e) => update({ previewValue: coerce(e.target.value, def.type) })}
            />
          </Field>
          <Field label="Fallback">
            <input
              className="input"
              value={String(def.fallback ?? '')}
              onChange={(e) => update({ fallback: coerce(e.target.value, def.type) })}
            />
          </Field>
        </>
      )}

      <Field label="Max length">
        <div className="row">
          <NumberInput value={def.width ?? 0} min={0} max={200} onChange={(v) => update({ width: v })} />
          <span className="faint" style={{ whiteSpace: 'nowrap' }}>
            {def.width ? 'cells' : 'no limit'}
          </span>
        </div>
      </Field>
      <Hint>
        A fixed length keeps the layout still: a longer value is cut off and a shorter one padded,
        so whatever sits beside it never shifts.
      </Hint>
      <Field label="Align">
        <Select
          ariaLabel="Align"
          value={def.align ?? 'left'}
          options={[
            { value: 'left' as const, label: 'left' },
            { value: 'center' as const, label: 'center' },
            { value: 'right' as const, label: 'right' },
          ]}
          onChange={(align) => update({ align })}
        />
      </Field>
      <Field label="Pad char">
        <input
          className="input mono"
          maxLength={1}
          value={def.padChar ?? ' '}
          onChange={(e) => update({ padChar: e.target.value || ' ' })}
        />
      </Field>
      <Field label="Format">
        <input
          className="input mono"
          placeholder="round:1 | suffix: ms"
          value={def.format ?? ''}
          onChange={(e) => update({ format: e.target.value })}
        />
      </Field>
      <Field label="Description">
        <input
          className="input"
          value={def.description ?? ''}
          onChange={(e) => update({ description: e.target.value })}
        />
      </Field>

      <ColorRuleEditor def={def} update={update} />
    </Modal>
  );
}

function ColorRuleEditor({
  def,
  update,
}: {
  def: VariableDef;
  update: (patch: Partial<VariableDef>) => void;
}): JSX.Element {
  const rules = def.colorRules ?? [];
  return (
    <div style={{ marginTop: 8 }}>
      <div className="row" style={{ marginBottom: 4 }}>
        <span className="faint">Colour rules</span>
        <span className="spacer" />
        <button
          className="icon-btn"
          title="Add rule"
          onClick={() => update({ colorRules: [...rules, { min: 0, max: 50, color: '#7dd3a0' }] })}
        >
          +
        </button>
      </div>
      {rules.map((rule, i) => (
        <div className="row" key={i} style={{ marginBottom: 4 }}>
          <input
            className="input"
            style={{ width: 56 }}
            placeholder="min"
            value={rule.min ?? ''}
            onChange={(e) => {
              const next = [...rules];
              next[i] = { ...rule, min: e.target.value === '' ? undefined : Number(e.target.value) };
              update({ colorRules: next });
            }}
          />
          <input
            className="input"
            style={{ width: 56 }}
            placeholder="max"
            value={rule.max ?? ''}
            onChange={(e) => {
              const next = [...rules];
              next[i] = { ...rule, max: e.target.value === '' ? undefined : Number(e.target.value) };
              update({ colorRules: next });
            }}
          />
          <input
            type="color"
            value={rule.color}
            style={{ width: 30, height: 26, padding: 0, border: '1px solid var(--border)', borderRadius: 4 }}
            onChange={(e) => {
              const next = [...rules];
              next[i] = { ...rule, color: e.target.value };
              update({ colorRules: next });
            }}
          />
          <button
            className="icon-btn"
            title="Remove rule"
            onClick={() => update({ colorRules: rules.filter((_, j) => j !== i) })}
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}

function coerce(raw: string, type: VariableType): string | number | boolean {
  if (type === 'boolean') return raw === 'true' || raw === '1';
  if (type === 'string' || type === 'array') return raw;
  const n = Number(raw);
  return Number.isFinite(n) && raw.trim() !== '' ? n : raw;
}
